/* The app's in-memory shapes and the mapping to/from the database rows (schema.sql).
   People are identified by id, never by name. A project's billableRate is null when no rate was decided (no row in
   project_rates) and 0 when the decided rate is $0 — never coerce one into the other. */
export type Role = 'owner' | 'admin' | 'bookkeeper' | 'member' | 'archived';
export interface CostRate { from: string; rate: number }          // from '' = since the beginning
export interface Member {
  id: string; name: string; email: string; role: Role; active: boolean; linked: boolean;
  importAliases: string[]; billRate: number | null; costRates: CostRate[];
  createdAt?: string | null;   // when added here (null = before 2026-10-11 / imported)
  archivedRole?: Role | null;  // archived people: the role they had (Restore gives it back; null = Member)
}
export interface Client { id: string; name: string; active: boolean; aliases: string[]; email: string; about: string; createdAt?: string | null }
export interface Project { id: string; clientId: string; name: string; defaultBillable: boolean | null; active: boolean; startDate: string | null; billableRate: number | null; createdAt?: string | null }
export interface Task { id: string; projectId: string; name: string; active: boolean; createdAt?: string | null }
export interface Entry {
  id: string; memberId: string; projectId: string; taskId: string; description: string; billable: boolean;
  date: string; startedAt: string; endedAt: string; so: number; durationSeconds: number; tags: string[]; source: string;
  updatedAt?: string;   // set by the database on every change (sync uses it); never sent back
}
export interface Assignment { projectId: string; memberId: string }   // project_members: this person may record time on this project
export interface Timer { memberId: string; projectId: string; taskId: string; description: string; billable: boolean; startedAt: string }
export interface Data { members: Member[]; clients: Client[]; projects: Project[]; tasks: Task[]; entries: Entry[] }

export const BEGINNING = '1900-01-01';  // member_cost_rates.effective_from for "since the beginning"
const num = (v: unknown): number | null => v === null || v === undefined ? null : Number(v);

/* ---------- rows -> app ---------- */
export function memberFromRows(m: any, money: any | undefined, cost: any[]): Member {
  return {
    id: m.id, name: m.name, email: m.email || '', role: m.role, active: m.active !== false, linked: !!m.auth_user_id,
    importAliases: m.import_aliases || [], billRate: money ? num(money.bill_rate) : null,
    createdAt: m.created_at ?? null, archivedRole: m.archived_role ?? null,
    costRates: cost.map(r => ({from: r.effective_from === BEGINNING ? '' : r.effective_from, rate: Number(r.rate)})).sort((a, b) => a.from.localeCompare(b.from)),
  };
}
export function clientFromRows(c: any, contact: any | undefined): Client {
  return {id: c.id, name: c.name, active: c.active !== false, aliases: c.aliases || [], email: contact?.email || '', about: contact?.about || '', createdAt: c.created_at ?? null};
}
export function projectFromRows(p: any, rate: any | undefined): Project {
  return {id: p.id, clientId: p.client_id, name: p.name, defaultBillable: p.default_billable, active: p.active !== false, startDate: p.start_date,
          billableRate: rate ? Number(rate.bill_rate) : null, createdAt: p.created_at ?? null};   // no row = not set; a row with 0 stays 0
}
export function taskFromRow(t: any): Task { return {id: t.id, projectId: t.project_id, name: t.name, active: t.active !== false, createdAt: t.created_at ?? null}; }
const wall = (s: string) => String(s).replace(' ', 'T').slice(0, 19);
export function entryFromRow(e: any): Entry {
  return {
    id: e.id, memberId: e.member_id, projectId: e.project_id || '', taskId: e.task_id || '', description: e.description || '',
    billable: !!e.billable, date: e.work_date, startedAt: wall(e.started_wall), endedAt: wall(e.ended_wall), so: e.utc_offset_min,
    durationSeconds: e.duration_seconds, tags: e.tags || [], source: e.source || 'manual', ...(e.updated_at ? {updatedAt: e.updated_at} : {}),
  };
}
export const ENTRY_COLS = 'id,member_id,project_id,task_id,description,billable,work_date,started_wall,ended_wall,utc_offset_min,duration_seconds,tags,source,updated_at';
export function entryToRow(e: Entry) {
  return {
    id: e.id, member_id: e.memberId, project_id: e.projectId || null, task_id: e.taskId || null, description: e.description || '',
    billable: e.billable, work_date: e.date, started_wall: e.startedAt, ended_wall: e.endedAt, utc_offset_min: e.so,
    duration_seconds: e.durationSeconds, tags: e.tags || [], source: e.source || 'manual',
  };
}

/** raw table rows (as the Data API returns them) -> app objects. Rows a role may not read simply arrive empty. */
export interface Rows { members: any[]; member_money: any[]; member_cost_rates: any[]; clients: any[]; client_contacts: any[]; projects: any[]; project_rates: any[]; tasks: any[] }
export function assemble(r: Rows): Omit<Data, 'entries'> {
  const money = new Map(r.member_money.map(x => [x.member_id, x])), contacts = new Map(r.client_contacts.map(x => [x.client_id, x]));
  const rates = new Map(r.project_rates.map(x => [x.project_id, x])), cost = new Map<string, any[]>();
  r.member_cost_rates.forEach(x => { const l = cost.get(x.member_id) || []; l.push(x); cost.set(x.member_id, l); });
  return {
    members: r.members.map(m => memberFromRows(m, money.get(m.id), cost.get(m.id) || [])).sort((a, b) => a.name.localeCompare(b.name)),
    clients: r.clients.map(c => clientFromRows(c, contacts.get(c.id))).sort((a, b) => a.name.localeCompare(b.name)),
    projects: r.projects.map(p => projectFromRows(p, rates.get(p.id))),
    tasks: r.tasks.map(taskFromRow),
  };
}

/* ---------- money (same rules as the prototype and the v_time_entries view) ---------- */
export const DEFAULT_COST = 15;
/** the member's cost per hour on a given day; null = unknown (no cost rows: not $0) */
export function costAt(m: Member | undefined, date: string): number | null {
  if (!m || !m.costRates.length) return null;
  const h = m.costRates; let best = h[0]; const d = date || '9999-12-31';
  for (const r of h) if ((r.from || '') <= d) best = r;
  return +best.rate || 0;
}
export function projRate(p: Project | undefined, m: Member | undefined): number { // project rate if set (0 is real); else the person's rate
  if (!p) return 0;
  if (p.billableRate !== null && p.billableRate !== undefined) return +p.billableRate || 0;
  return m ? (+(m.billRate ?? 0) || 0) : 0;
}

/* ---------- roles ---------- */
export const ROLE_LABEL: Record<string, string> = {owner: 'Owner', admin: 'Admin', bookkeeper: 'Bookkeeper', member: 'Member', archived: 'Archived'};
const PERM: Record<string, Role[]> = {
  seeAll: ['owner', 'admin', 'bookkeeper'], seeMoney: ['owner', 'admin', 'bookkeeper'],
  trackTime: ['owner', 'admin', 'member', 'bookkeeper'], editAll: ['owner', 'admin'],   // trackTime = their OWN time only; editAll = everyone's
  manageStructure: ['owner', 'admin'], editMembers: ['owner', 'admin'], invite: ['owner', 'admin'], manageRoles: ['owner'],
};
/** what the screen shows; the database (RLS) enforces the same rules independently */
export function can(role: Role | null | undefined, action: keyof typeof PERM | string): boolean { return !!role && (PERM[action] || []).includes(role); }
